import React from 'react';
import { Building2, CalendarDays, Download, RefreshCw } from 'lucide-react';
import { cn } from '../../utils/cn';
import { AdminFilterSelect } from '../admin/AdminDataFilters';
import { Spinner } from '../common/LoadingState';

interface AssemblyOption {
  id: string;
  title: string;
  scheduledDate?: string;
}

interface ComplexOption {
  id: string;
  name: string;
}

interface DelegationsContextToolbarProps {
  mode: 'admin' | 'manager' | 'builder';
  complexName?: string | null;
  complexes?: ComplexOption[];
  selectedComplexId?: string;
  onComplexChange?: (complexId: string) => void;
  assemblies: AssemblyOption[];
  selectedAssemblyId: string;
  onAssemblyChange: (assemblyId: string) => void;
  onRefresh: () => void;
  refreshing?: boolean;
  onExport?: () => void;
  exportDisabled?: boolean;
  exporting?: boolean;
  title?: string;
  helperText?: string;
  ariaLabel?: string;
}

const focusRing =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2';

export const DelegationsContextToolbar: React.FC<DelegationsContextToolbarProps> = ({
  mode,
  complexName,
  complexes = [],
  selectedComplexId = '',
  onComplexChange,
  assemblies,
  selectedAssemblyId,
  onAssemblyChange,
  onRefresh,
  refreshing = false,
  onExport,
  exportDisabled = false,
  exporting = false,
  title = 'Delegaciones y poderes',
  helperText,
  ariaLabel = 'Contexto de delegaciones',
}) => {
  const showComplexPicker = mode === 'admin' || mode === 'builder';
  const resolvedHelperText =
    helperText ??
    (mode === 'admin'
      ? 'Selecciona un conjunto y una asamblea para auditar poderes delegados, coeficientes entregados y revocaciones.'
      : mode === 'builder'
        ? 'Descarga los poderes firmados uno a uno por conjunto y asamblea.'
        : 'Revisa los poderes vigentes por asamblea.');

  const selectedAssembly = assemblies.find((assembly) => assembly.id === selectedAssemblyId);
  const selectedAssemblyLabel = selectedAssembly
    ? `${selectedAssembly.title}${selectedAssembly.scheduledDate ? ` — ${selectedAssembly.scheduledDate}` : ''}`
    : undefined;

  return (
    <section
      aria-label={ariaLabel}
      className="overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm"
    >
      <div className="flex flex-col gap-5 p-4 sm:p-5 lg:flex-row lg:items-stretch lg:justify-between lg:gap-6">
        <div className="min-w-[240px] flex-1 border-b border-slate-100 pb-4 lg:min-w-[320px] lg:border-b-0 lg:border-r lg:pb-0 lg:pr-6">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
            Gestión
          </p>
          <h2 className="mt-1 text-base font-semibold tracking-tight text-slate-900">
            {title}
          </h2>
          {mode === 'manager' && complexName ? (
            <p className="mt-1 text-sm font-medium text-slate-800">{complexName}</p>
          ) : null}
          <p className="mt-1.5 max-w-lg text-sm leading-relaxed text-slate-600">
            {resolvedHelperText}
          </p>
        </div>

        <div className="flex flex-col justify-center gap-3 lg:w-[460px] lg:max-w-[46%]">
          <div
            className="grid grid-cols-1 gap-2 rounded-xl border border-slate-200 bg-slate-50 p-2 sm:grid-cols-2"
            role="group"
            aria-label="Filtros de delegaciones"
          >
            {showComplexPicker ? (
              <label className="block min-w-0">
                <span className="mb-1 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-500">
                  <Building2 className="h-3.5 w-3.5" aria-hidden="true" />
                  Conjunto
                </span>
                <AdminFilterSelect
                  value={selectedComplexId}
                  onChange={(value) => onComplexChange?.(value)}
                  icon={Building2}
                  truncate
                  aria-label="Seleccionar conjunto"
                >
                  <option value="">Seleccionar conjunto</option>
                  {complexes.map((complex) => (
                    <option key={complex.id} value={complex.id}>
                      {complex.name}
                    </option>
                  ))}
                </AdminFilterSelect>
              </label>
            ) : (
              <label className="block min-w-0 sm:col-span-2">
                <span className="mb-1 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-500">
                  <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
                  Asamblea
                </span>
                <AdminFilterSelect
                  value={selectedAssemblyId}
                  onChange={onAssemblyChange}
                  icon={CalendarDays}
                  disabled={assemblies.length === 0}
                  truncate
                  title={selectedAssemblyLabel}
                  aria-label="Seleccionar asamblea"
                >
                  {assemblies.length === 0 && <option value="">Sin asambleas</option>}
                  {assemblies.map((assembly) => (
                    <option key={assembly.id} value={assembly.id}>
                      {assembly.title}
                      {assembly.scheduledDate ? ` — ${assembly.scheduledDate}` : ''}
                    </option>
                  ))}
                </AdminFilterSelect>
              </label>
            )}

            {showComplexPicker ? (
              <label className="block min-w-0">
                <span className="mb-1 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-500">
                  <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
                  Asamblea
                </span>
                <AdminFilterSelect
                  value={selectedAssemblyId}
                  onChange={onAssemblyChange}
                  icon={CalendarDays}
                  disabled={assemblies.length === 0}
                  truncate
                  title={selectedAssemblyLabel}
                  aria-label="Seleccionar asamblea"
                >
                  {assemblies.length === 0 && <option value="">Sin asambleas</option>}
                  {assemblies.map((assembly) => (
                    <option key={assembly.id} value={assembly.id}>
                      {assembly.title}
                      {assembly.scheduledDate ? ` — ${assembly.scheduledDate}` : ''}
                    </option>
                  ))}
                </AdminFilterSelect>
              </label>
            ) : null}
          </div>

          <div className={cn('grid gap-2', onExport ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1')}>
            {onExport ? (
              <button
                type="button"
                onClick={onExport}
                disabled={exportDisabled || exporting || refreshing}
                className={cn(
                  'inline-flex w-full items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-medium text-emerald-800 transition-colors',
                  'hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-55',
                  focusRing
                )}
              >
                {exporting ? (
                  <Spinner size="sm" className="text-emerald-700" />
                ) : (
                  <Download className="h-4 w-4 text-emerald-700" aria-hidden="true" />
                )}
                <span>{exporting ? 'Exportando...' : 'Exportar Excel'}</span>
              </button>
            ) : null}

            <button
              type="button"
              onClick={onRefresh}
              disabled={refreshing}
              className={cn(
                'inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors',
                'hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-55',
                focusRing
              )}
            >
              {refreshing ? (
                <Spinner size="sm" className="text-blue-600" />
              ) : (
                <RefreshCw className="h-4 w-4 text-blue-600" aria-hidden="true" />
              )}
              <span>{refreshing ? 'Actualizando...' : 'Actualizar'}</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
